import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Dental test cart context – same shape as LabTestCartContext for identical flow.
 * Item shape: { id, name, ... }. selectedLocation and selectedLab for booking.
 * ongoingPrescriptionDigitisation: { requestId, status: 'digitizing'|'accepted'|'rejected', message } | null – persists across page navigation.
 */
const DentalTestCartContext = createContext(null);

const STORAGE_KEY_DENTAL_DIGITISATION = 'dental_prescription_digitisation';
const STORAGE_KEY_DENTAL_DIGITISATION_PATIENT = 'dental_prescription_digitisation_patient_id';
const STORAGE_KEY_DENTAL_DIGITISATION_CENTRE = 'dental_prescription_digitisation_centre';
const STORAGE_KEY_DENTAL_PROCESSOR_DETAILS = 'dental_processor_digitisation_details';
const STORAGE_KEY_DENTAL_CONSULT_DRAFT = 'dental_consultation_draft';

function loadDigitisationFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_DENTAL_DIGITISATION);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.requestId === 'string' && typeof parsed.status === 'string') return parsed;
  } catch (_) {}
  return null;
}

function loadDigitisedPatientIdFromStorage() {
  try {
    return localStorage.getItem(STORAGE_KEY_DENTAL_DIGITISATION_PATIENT) || null;
  } catch (_) {}
  return null;
}

function loadDigitisedCentreFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_DENTAL_DIGITISATION_CENTRE);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (_) {}
  return null;
}

function loadProcessorDetailsFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_DENTAL_PROCESSOR_DETAILS);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (_) {}
  return null;
}

function loadConsultDraftFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_DENTAL_CONSULT_DRAFT);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch (_) {}
  return null;
}

function saveDigitisationToStorage(value) {
  try {
    if (value == null) localStorage.removeItem(STORAGE_KEY_DENTAL_DIGITISATION);
    else localStorage.setItem(STORAGE_KEY_DENTAL_DIGITISATION, JSON.stringify(value));
  } catch (_) {}
}

function saveDigitisedPatientIdToStorage(patientId) {
  try {
    if (patientId == null || patientId === '') localStorage.removeItem(STORAGE_KEY_DENTAL_DIGITISATION_PATIENT);
    else localStorage.setItem(STORAGE_KEY_DENTAL_DIGITISATION_PATIENT, String(patientId));
  } catch (_) {}
}

function saveDigitisedCentreToStorage(centre) {
  try {
    if (centre == null) localStorage.removeItem(STORAGE_KEY_DENTAL_DIGITISATION_CENTRE);
    else localStorage.setItem(STORAGE_KEY_DENTAL_DIGITISATION_CENTRE, JSON.stringify(centre));
  } catch (_) {}
}

function saveProcessorDetailsToStorage(details) {
  try {
    if (details == null) localStorage.removeItem(STORAGE_KEY_DENTAL_PROCESSOR_DETAILS);
    else localStorage.setItem(STORAGE_KEY_DENTAL_PROCESSOR_DETAILS, JSON.stringify(details));
  } catch (_) {}
}

function saveConsultDraftToStorage(draft) {
  try {
    if (draft == null) localStorage.removeItem(STORAGE_KEY_DENTAL_CONSULT_DRAFT);
    else localStorage.setItem(STORAGE_KEY_DENTAL_CONSULT_DRAFT, JSON.stringify(draft));
  } catch (_) {}
}

export function DentalTestCartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [selectedLocation, setSelectedLocationState] = useState(null);
  const [selectedLab, setSelectedLabState] = useState(null);
  const [ongoingPrescriptionDigitisation, setOngoingPrescriptionDigitisationState] = useState(() => loadDigitisationFromStorage());
  const [digitizedPrescriptionPatientId, setDigitizedPrescriptionPatientIdState] = useState(() => loadDigitisedPatientIdFromStorage());
  const [digitizedPrescriptionCentre, setDigitizedPrescriptionCentreState] = useState(() => loadDigitisedCentreFromStorage());
  const [digitizedProcessorDetails, setDigitizedProcessorDetailsState] = useState(() => loadProcessorDetailsFromStorage());
  const [consultDraft, setConsultDraftState] = useState(() => loadConsultDraftFromStorage());

  const setOngoingPrescriptionDigitisation = useCallback((value) => {
    setOngoingPrescriptionDigitisationState(value);
    saveDigitisationToStorage(value);
  }, []);

  const setDigitizedPrescriptionPatientId = useCallback((patientId) => {
    setDigitizedPrescriptionPatientIdState(patientId);
    saveDigitisedPatientIdToStorage(patientId);
  }, []);

  const setDigitizedPrescriptionCentre = useCallback((centre) => {
    setDigitizedPrescriptionCentreState(centre && typeof centre === 'object' ? centre : null);
    saveDigitisedCentreToStorage(centre && typeof centre === 'object' ? centre : null);
  }, []);

  const setDigitizedProcessorDetails = useCallback((details) => {
    setDigitizedProcessorDetailsState(details && typeof details === 'object' ? details : null);
    saveProcessorDetailsToStorage(details && typeof details === 'object' ? details : null);
  }, []);

  const setConsultDraft = useCallback((valueOrUpdater) => {
    setConsultDraftState((prev) => {
      const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
      const normalized = next && typeof next === 'object' ? next : null;
      saveConsultDraftToStorage(normalized);
      return normalized;
    });
  }, []);
  const setSelectedLocation = useCallback((location) => {
    setSelectedLocationState(location && typeof location === 'object' ? location : null);
  }, []);
  const setSelectedLab = useCallback((lab) => {
    setSelectedLabState(lab && typeof lab === 'object' ? lab : null);
  }, []);

  const addItem = useCallback((item) => {
    if (!item?.id) return;
    setItems((prev) => {
      if (prev.some((i) => i.id === item.id)) return prev;
      return [...prev, { id: item.id, name: item.name || '', ...item }];
    });
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const setCartItems = useCallback((newItems) => {
    setItems(Array.isArray(newItems) ? newItems : []);
  }, []);

  const value = {
    items,
    addItem,
    removeItem,
    clearCart,
    setCartItems,
    count: items.length,
    selectedLocation,
    setSelectedLocation,
    selectedLab,
    setSelectedLab,
    ongoingPrescriptionDigitisation: ongoingPrescriptionDigitisation,
    setOngoingPrescriptionDigitisation,
    digitizedPrescriptionPatientId: digitizedPrescriptionPatientId,
    setDigitizedPrescriptionPatientId,
    digitizedPrescriptionCentre,
    setDigitizedPrescriptionCentre,
    digitizedProcessorDetails,
    setDigitizedProcessorDetails,
    consultDraft,
    setConsultDraft,
  };

  return (
    <DentalTestCartContext.Provider value={value}>
      {children}
    </DentalTestCartContext.Provider>
  );
}

export function useDentalTestCart() {
  const ctx = useContext(DentalTestCartContext);
  if (!ctx) throw new Error('useDentalTestCart must be used within DentalTestCartProvider');
  return ctx;
}
